/**
 * 带缓存的请求工具
 *
 * 功能特性：
 * 1. 自动缓存GET请求
 * 2. 请求去重
 * 3. 可配置的缓存策略
 * 4. 支持缓存失效
 */

import { get, post, put, del, ApiResponse } from '@/utils/request';
import { apiCache } from './apiCache';
import { getCacheTTLForUrl, shouldCacheUrl } from '@/utils/constants/cacheConfig';

// 缓存选项接口
export interface CacheOptions {
  ttl?: number; // 缓存时间（毫秒）
  useCache?: boolean; // 是否使用缓存
  forceRefresh?: boolean; // 强制刷新缓存
}

// 请求选项接口
export interface RequestOptions extends CacheOptions {
  params?: any;
  data?: any;
}

/**
 * 带缓存的GET请求
 */
export const cachedGet = async <T = any>(
  url: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const {
    params,
    ttl = getCacheTTLForUrl(url), // 使用配置的TTL
    useCache = shouldCacheUrl(url, 'GET'), // 使用配置的缓存策略
    forceRefresh = false,
  } = options;

  // 生成请求键用于去重
  const requestKey = `GET:${url}:${JSON.stringify(params || {})}`;

  // 如果不使用缓存或强制刷新，直接请求
  if (!useCache || forceRefresh) {
    return apiCache.deduplicateRequest(requestKey, () => get<T>(url, params));
  }

  // 尝试从缓存获取
  const cached = apiCache.get<ApiResponse<T>>(url, params);
  if (cached) {
    return cached;
  }

  // 缓存未命中，发起请求并缓存结果
  return apiCache.deduplicateRequest(requestKey, async () => {
    const response = await get<T>(url, params);

    // 只缓存成功的响应
    if (response.code === 0) {
      apiCache.set(url, response, params, ttl);
    }

    return response;
  });
};

/**
 * 带缓存失效的POST请求
 */
export const cachedPost = async <T = any>(
  url: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const { data, params } = options;

  const requestKey = `POST:${url}:${JSON.stringify({ data, params })}`;

  return apiCache.deduplicateRequest(requestKey, async () => {
    const response = await post<T>(url, data);

    // POST请求成功后，清理相关的GET缓存
    if (response.code === 0) {
      invalidateRelatedCache(url);
    }

    return response;
  });
};

/**
 * 带缓存失效的PUT请求
 */
export const cachedPut = async <T = any>(
  url: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const { data, params } = options;

  const requestKey = `PUT:${url}:${JSON.stringify({ data, params })}`;

  return apiCache.deduplicateRequest(requestKey, async () => {
    const response = await put<T>(url, data);

    // PUT请求成功后，清理相关的GET缓存
    if (response.code === 0) {
      invalidateRelatedCache(url);
    }

    return response;
  });
};

/**
 * 带缓存失效的DELETE请求
 */
export const cachedDelete = async <T = any>(
  url: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const { params } = options;

  const requestKey = `DELETE:${url}:${JSON.stringify(params || {})}`;

  return apiCache.deduplicateRequest(requestKey, async () => {
    const response = await del<T>(url, params);

    // DELETE请求成功后，清理相关的GET缓存
    if (response.code === 0) {
      invalidateRelatedCache(url);
    }

    return response;
  });
};

/**
 * 清理相关缓存
 * 根据URL模式清理相关的缓存项
 */
const invalidateRelatedCache = (url: string): void => {
  // 提取基础路径
  const basePath = url.split('?')[0];
  const pathSegments = basePath.split('/').filter(Boolean);

  // 定义需要清理的相关路径模式
  const invalidationPatterns: string[] = [];

  // 根据不同的API路径定义清理策略
  if (pathSegments.includes('asset')) {
    // 资产相关操作，清理资产列表缓存
    invalidationPatterns.push('/asset/list');
    invalidationPatterns.push('/asset/');
  }

  if (pathSegments.includes('generation')) {
    // 生成相关操作，清理生成历史缓存
    invalidationPatterns.push('/generation/');
    invalidationPatterns.push('/asset/list'); // 生成完成后资产列表也需要更新
  }

  if (pathSegments.includes('group')) {
    // 分组相关操作，清理分组和资产缓存
    invalidationPatterns.push('/group/');
    invalidationPatterns.push('/asset/list');
  }

  // 执行缓存清理
  invalidationPatterns.forEach(pattern => {
    clearCacheByPattern(pattern);
  });

  if (process.env.NODE_ENV === 'development') {
    console.log(`[API缓存] 清理与 ${url} 相关的缓存，模式: ${invalidationPatterns.join(', ')}`);
  }
};

/**
 * 根据URL模式清理缓存
 */
const clearCacheByPattern = (pattern: string): void => {
  // 这里需要访问apiCache的内部方法，我们可以添加一个公共方法
  // 暂时使用简单的实现
  if (pattern.endsWith('/')) {
    // 模糊匹配，清理所有包含该路径的缓存
    // 注意：这需要apiCache支持模式匹配清理
    // 目前先实现精确匹配
    apiCache.delete(pattern);
  } else {
    // 精确匹配
    apiCache.delete(pattern);
  }
};

/**
 * 手动清理特定URL的缓存
 */
export const invalidateCache = (url: string, params?: any): void => {
  apiCache.delete(url, params);
};

/**
 * 清理所有缓存
 */
export const clearAllCache = (): void => {
  apiCache.clear();
};

/**
 * 获取缓存统计信息
 */
export const getCacheStats = () => {
  return apiCache.getStats();
};

/**
 * 预加载数据到缓存
 */
export const preloadCache = async <T = any>(
  url: string,
  params?: any,
  ttl?: number
): Promise<void> => {
  try {
    await cachedGet<T>(url, { params, ttl, forceRefresh: true });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[API缓存] 预加载失败: ${url}`, error);
    }
  }
};

/**
 * 批量预加载
 */
export const batchPreload = async (
  requests: Array<{ url: string; params?: any; ttl?: number }>
): Promise<void> => {
  const promises = requests.map(
    ({ url, params, ttl }) => preloadCache(url, params, ttl).catch(() => {}) // 忽略单个请求的错误
  );

  await Promise.all(promises);

  if (process.env.NODE_ENV === 'development') {
    console.log(`[API缓存] 批量预加载完成，共 ${requests.length} 个请求`);
  }
};

// 导出缓存增强的请求方法
export const cachedRequest = {
  get: cachedGet,
  post: cachedPost,
  put: cachedPut,
  delete: cachedDelete,
  invalidate: invalidateCache,
  clear: clearAllCache,
  stats: getCacheStats,
  preload: preloadCache,
  batchPreload,
};

export default cachedRequest;
